import React from 'react';
import { useFeatureSet } from '@shared/core/featureSet/useFeatureSet';
import { useEffect } from 'react';
import { useGuestSiteRedirection } from '../../common/utils/useGuestSiteRedirection';
import { useGuestSiteFeatureSetHealth, wrapWithGuestSiteFeatureSetHealthProvider } from './GuestSiteFeatureSet.health';
import { useIsGuestSiteSsrEnabled } from '@apps/guest/shared/hooks/useIsGuestSiteSsrEnabled';
import { JoyLogoLoader } from '@shared/components/JoyLogoLoader';
import type { StaticContext } from '@react-router';

/**
 * Component responsible for resolving the FeatureSet and redirection.
 *
 * When websiteFullySsrGuestSite is enabled, auth and data resolve during SSR/hydration
 * so no loading gate is needed. When disabled, the JoyLogoLoader gates rendering until
 * the feature set is initialized and redirection is resolved (original behavior).
 */
type GuestSiteFeatureSetProps = Readonly<{
  loadingGuestSite?: boolean;
  // Holds rendering even when full SSR is enabled (unlike loadingGuestSite, which the SSR
  // path bypasses). Set while unlock state is indeterminate on a potentially-gated route —
  // rendering the layout would flash unprotected chrome at locked visitors, rendering the
  // gate would flash it at unlocked ones (see GuestSiteBody's isGateResolutionPending).
  holdForGateResolution?: boolean;
  staticContext?: StaticContext;
}>;

export const GuestSiteFeatureSet: React.FC<GuestSiteFeatureSetProps> = wrapWithGuestSiteFeatureSetHealthProvider(
  ({ children, loadingGuestSite, holdForGateResolution, staticContext }) => {
    const { onLoadComplete, onLoadFailure } = useGuestSiteFeatureSetHealth();
    const { isInitialized: featureSetInitialized, features, hasErrors: featureSetError } = useFeatureSet();
    const { waitingRedirection: waitingFeaturesetRedirection } = useGuestSiteRedirection(features, staticContext);
    const isFullySsrEnabled = useIsGuestSiteSsrEnabled();

    useEffect(() => {
      if (featureSetError) {
        onLoadFailure(featureSetError);
      }
    }, [featureSetError, onLoadFailure]);

    useEffect(() => {
      if (featureSetInitialized && !waitingFeaturesetRedirection) {
        onLoadComplete();
      }
    }, [featureSetInitialized, waitingFeaturesetRedirection, onLoadComplete]);

    // When full SSR is disabled, gate rendering with JoyLogoLoader (original behavior).
    // In CSR/preview mode, fonts and theme aren't preloaded so we need the full loading gate.
    // holdForGateResolution applies in BOTH modes: the unlock query is ssr:false, so its
    // indeterminate window exists on the SSR path too (server + hydration render the same hold).
    if (holdForGateResolution || (!isFullySsrEnabled && (loadingGuestSite || !featureSetInitialized || waitingFeaturesetRedirection))) {
      return <JoyLogoLoader loaderKey="guestsitefeatureset" />;
    }

    return <>{children}</>;
  },
);
